/**
 * Master Registry
 *
 * Collects master registrations during module init phase.
 * Plugins and modules call register() to add their reference data.
 * Seed runs after all modules have registered their masters.
 */

import type { ModuleContext } from '@gzl10/nexus-sdk'

export interface MasterEntry {
  code: string
  label: string | { en?: string; es?: string; [key: string]: string | undefined }
  order?: number
  is_active?: boolean
  metadata?: Record<string, unknown>
}

export interface MasterRegistration {
  type: string
  entries: MasterEntry[]
  seed: 'if-empty' | 'always'
}

/**
 * Creates a registry for collecting and seeding master data.
 *
 * @example
 * ```typescript
 * // In a plugin's init():
 * const registry = ctx.services.get('masters')
 * registry.register('blood-types', [
 *   { code: 'A+', label: { en: 'A positive', es: 'A positivo' } },
 *   { code: 'O-', label: { en: 'O negative', es: 'O negativo' } },
 * ])
 * ```
 */
export function createMasterRegistry() {
  const registrations: MasterRegistration[] = []

  return {
    /**
     * Register master entries for a type. Called by plugins/modules in init().
     */
    register(type: string, entries: MasterEntry[], options?: { seed?: 'if-empty' | 'always' }) {
      registrations.push({
        type,
        entries,
        seed: options?.seed ?? 'if-empty'
      })
    },

    /**
     * Seed all registered masters into the DB. Called once after all modules init.
     */
    async seed(ctx: ModuleContext) {
      for (const reg of registrations) {
        const { type, entries, seed } = reg

        if (seed === 'if-empty') {
          const existing = await ctx.db.knex('masters').where({ type }).first()
          if (existing) continue
        }

        const rows = entries.map((entry, i) => ({
          id: `${type}:${entry.code}`,
          type,
          code: entry.code,
          label: JSON.stringify(typeof entry.label === 'string' ? { en: entry.label } : entry.label),
          order: entry.order ?? i,
          is_active: entry.is_active ?? true,
          metadata: entry.metadata ? JSON.stringify(entry.metadata) : null
        }))

        if (seed === 'always') {
          for (const row of rows) {
            await ctx.db.knex('masters')
              .insert(row)
              .onConflict(['type', 'code'])
              .merge()
          }
        } else {
          await ctx.db.knex('masters')
            .insert(rows)
            .onConflict(['type', 'code'])
            .ignore()
        }
      }
    },

    /** Get all registrations (for testing/debugging) */
    getRegistrations() {
      return [...registrations]
    }
  }
}

export type MasterRegistry = ReturnType<typeof createMasterRegistry>
